/**
 * schema.org structured-data builders (single source of truth for JSON-LD).
 *
 * Mirrors the pattern used in the Next.js reference repos (lib/structured-data.ts):
 * small pure builders that return plain schema objects, composed per page and
 * handed to <SEO jsonLd={...} /> which injects them into <head> (captured by the
 * Puppeteer SSG prerender). Do NOT inline JSON-LD in pages — add/extend a builder here.
 */

import { SITE_CONFIG } from '@/config/site';

export const SITE_URL = SITE_CONFIG.url;
const LOGO = `${SITE_URL}${SITE_CONFIG.logo}`;

/** Resolve a site-relative path to an absolute URL. */
export function absoluteUrl(path = '/'): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Organization — powers the Google logo/knowledge panel. */
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_CONFIG.name,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: LOGO,
      width: 512,
      height: 512,
    },
    image: LOGO,
    description:
      'IT services and software development: managed IT & support, cloud & infrastructure, cybersecurity & identity, data & databases, AI & automation, and web & software development.',
    email: SITE_CONFIG.email,
    telephone: SITE_CONFIG.phone,
    sameAs: [SITE_CONFIG.social.linkedin, SITE_CONFIG.social.twitter],
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE_CONFIG.address.streetAddress,
      addressLocality: SITE_CONFIG.address.locality,
      postalCode: SITE_CONFIG.address.postalCode,
      addressCountry: SITE_CONFIG.address.country,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: SITE_CONFIG.email,
      telephone: SITE_CONFIG.phone,
      availableLanguage: [...SITE_CONFIG.availableLanguages],
    },
  };
}

/** WebSite — enables sitelinks search box eligibility. */
export function webSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_CONFIG.name,
    url: SITE_URL,
    inLanguage: [...SITE_CONFIG.locales],
    publisher: { '@type': 'Organization', name: SITE_CONFIG.name, url: SITE_URL },
  };
}

export interface BreadcrumbItem {
  name: string;
  path: string;
}

/** BreadcrumbList — add on nested routes (service/article/etc.). */
export function breadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export interface ServiceSchemaInput {
  name: string;
  description: string;
  path: string;
  serviceType?: string;
}

/** Service — one per /services/:id page. */
export function serviceSchema({ name, description, path, serviceType }: ServiceSchemaInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    ...(serviceType ? { serviceType } : {}),
    url: absoluteUrl(path),
    provider: { '@type': 'Organization', name: SITE_CONFIG.name, url: SITE_URL },
    areaServed: { '@type': 'Country', name: SITE_CONFIG.areaServed },
  };
}

export interface ArticleSchemaInput {
  title: string;
  description: string;
  path: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
}

/** Article — one per /articles/:slug page. */
export function articleSchema({
  title,
  description,
  path,
  datePublished,
  dateModified,
  image,
}: ArticleSchemaInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: absoluteUrl(path),
    mainEntityOfPage: { '@type': 'WebPage', '@id': absoluteUrl(path) },
    image: image ? absoluteUrl(image) : LOGO,
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : datePublished ? { dateModified: datePublished } : {}),
    author: { '@type': 'Organization', name: SITE_CONFIG.name, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: SITE_CONFIG.name,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: LOGO, width: 512, height: 512 },
    },
  };
}

export interface FaqItem {
  question: string;
  answer: string;
}

/** FAQPage — add where a page has a real Q&A block. */
export function faqSchema(items: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}
